<template>
    <OnboardingLayout v-bind="appSharedData.getOnboardingLayoutProps()">
        <template #content>
            <OnboardingStepContainer
                :showStepsIndicator="true"
                :stepNumber="3"
            >
                <template #title>Set up your booking page</template>
                <template #subtitle>
                    Start with the basics. You can add more branding later.
                </template>
                <template #content>
                    <div class="w-full flex flex-col lg:flex-row gap-4">
                        <div class="w-full lg:w-2/5">
                            <Card>
                                <template #content>
                                    <ApiForm
                                        method="POST"
                                        :url="
                                            RoutePathAppEnum.Onboarding_Step3_BookingPage
                                        "
                                        ref="apiFormRef"
                                    >
                                        <CompanyInformationForm
                                            :showWarning="false"
                                            :company="
                                                appSharedData.getAuthCompany()
                                            "
                                            :errors="
                                                apiFormRef?.response?.getErrors() ??
                                                new ApiFormErrors({})
                                            "
                                            @onDisplayNameUpdated="
                                                handleDisplayNameUpdated(
                                                    $event.value,
                                                )
                                            "
                                            @onSlugUpdated="
                                                handleBookingPageSlugUpdated(
                                                    $event.value,
                                                )
                                            "
                                            @onLogoUpdated="
                                                handleLogoImageUpdated(
                                                    $event.value,
                                                )
                                            "
                                            @onCoverUpdated="
                                                handleCoverImageUpdated(
                                                    $event.value,
                                                )
                                            "
                                        />
                                        <div class="text-xs text-gray-400 mt-4">
                                            These details can be updated later
                                            on.
                                        </div>
                                    </ApiForm>
                                </template>
                            </Card>
                        </div>

                        <div class="w-full lg:w-3/5">
                            <MockBookingForm
                                :bookingPageUrl="dummyMockBookingPageUrl"
                                :displayName="dummyMockDisplayName"
                                :profileImageUrl="dummyLogoImageUrl"
                                :coverImageUrl="dummyCoverImageUrl"
                                :serviceTitle="dummyMockServiceTitle"
                                :serviceDescription="
                                    dummyMockServiceDescription
                                "
                            />
                        </div>
                    </div>
                </template>
            </OnboardingStepContainer>
        </template>

        <template #footerRight>
            <Button
                @click="apiFormRef?.submit()"
                iconName="arrow-right"
                color="primary"
                :iconRight="true"
                :loading="apiFormRef?.loading.valueOf()"
            >
                Continue
            </Button>
        </template>
    </OnboardingLayout>
</template>

<script setup lang="ts">
    import { computed, ref } from 'vue'
    import OnboardingLayout from '@/Shared/Layout/Onboarding/OnboardingLayout.vue'
    import Button from '@/Shared/Component/Button.vue'
    import ApiForm from '@/Shared/Component/Forms/ApiForm.vue'
    import OnboardingStepContainer from '@/App/Company/Component/OnboardingStepContainer.vue'
    import CompanyInformationForm from '@/App/Company/Component/CompanyInformationForm.vue'
    import type { ApiFormRefType } from '@/Shared/AutoGenerated/Types'
    import Card from '@/Shared/Component/Card.vue'
    import ApiFormErrors from '@/Shared/Component/Forms/ApiFormErrors'
    import AppSharedData from '@/Shared/Entity/AppSharedData'
    import Onboarding from '@/App/Company/Entity/Onboarding'
    import { RouteParamKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'
    import {
        BookingReasonEnumData,
        type OnboardingRawData,
    } from '@/Shared/AutoGenerated/Main'
    import {
        RoutePathAppEnum,
        RoutePathBookingFormEnum,
    } from '@/Shared/AutoGenerated/RoutePathEnum'
    import MockBookingForm from '@/App/Company/Component/MockBookingForm.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface ShowOnboardingStepBookingPageProps {
        appSharedDataData: any
        onboardingRawData: OnboardingRawData
    }

    const props = defineProps<ShowOnboardingStepBookingPageProps>()
    const appSharedData = AppSharedData.hydrate(props.appSharedDataData)
    const onboarding = Onboarding.hydrateSingle(props.onboardingRawData)
    const authCompany = appSharedData.getAuthCompany()
    const authUser = appSharedData.getAuthUser()
    const apiFormRef = ref<ApiFormRefType>(null)

    // ----------
    // Display name
    // ----------

    const dummyDisplayName = ref<string>(authCompany.displayName)

    const dummyMockDisplayName = computed(() => {
        const trimmedDisplayName = dummyDisplayName.value.trim()

        return trimmedDisplayName || authCompany.displayName
    })

    const handleDisplayNameUpdated = (newValue: string) => {
        dummyDisplayName.value = newValue
    }

    // ----------
    // Logo
    // ----------

    const dummyLogoImage = ref<string | null>(
        authCompany.logoImage?.url ?? authUser.profilePicture?.url ?? null,
    )

    const dummyLogoImageUrl = computed(() => {
        return (
            dummyLogoImage.value ?? authUser.getProfilePictureUrlWithFallback()
        )
    })

    const handleLogoImageUpdated = (newLogoUrl: string | null) => {
        dummyLogoImage.value = newLogoUrl
    }

    // ----------
    // Cover image
    // ----------

    const dummyCoverImageUrl = ref<string | undefined>(
        authCompany.coverImage?.url,
    )

    const handleCoverImageUpdated = (newCoverImageUrl: string | null) => {
        dummyCoverImageUrl.value = newCoverImageUrl ?? undefined
    }

    // ----------
    // Page slug
    // ----------

    const dummyBookingPageSlug = ref<string>(authCompany.slug)
    const genericServiceTitle = '1:1 Session'
    const genericServiceDescription =
        'A focused session to discuss goals, answer questions, and decide on the best next steps.'

    const dummyMockServiceTitle = computed(() => {
        return onboarding.surveyBookingReason === null
            ? genericServiceTitle
            : BookingReasonEnumData[onboarding.surveyBookingReason].label
    })

    const dummyMockServiceDescription = computed(() => {
        return onboarding.surveyBookingReason === null
            ? genericServiceDescription
            : BookingReasonEnumData[onboarding.surveyBookingReason].description
    })

    const dummyMockBookingPageUrl = computed(() => {
        const trimmedBookingPageSlug = dummyBookingPageSlug.value.trim()
        const bookingPageSlug = trimmedBookingPageSlug || authCompany.slug

        return urlFactory.makeCompanyBookingFormUrl(bookingPageSlug)
    })

    const handleBookingPageSlugUpdated = (newValue: string) => {
        dummyBookingPageSlug.value = newValue
    }

    const urlFactory = new UrlFactory()
</script>
